import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import type { RsvpQuestion } from "@/components/card/RsvpQuestionBuilder";

interface Props {
  questions: RsvpQuestion[];
  answers: Record<string, string | string[]>;
  onChange: (answers: Record<string, string | string[]>) => void;
}

const RsvpPreferenceForm = ({ questions, answers, onChange }: Props) => {
  const { t } = useTranslation();

  const setAnswer = (qId: string, value: string | string[]) => {
    onChange({ ...answers, [qId]: value });
  };

  const toggleMulti = (qId: string, option: string) => {
    const current = (answers[qId] as string[]) || [];
    const next = current.includes(option)
      ? current.filter((o) => o !== option)
      : [...current, option];
    setAnswer(qId, next);
  };

  if (!questions.length) return null;

  return (
    <div className="space-y-4 border-t border-border pt-3">
      <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
        {t("cardView.preferences", { defaultValue: "Your preferences" })}
      </p>
      {questions.map((q) => (
        <div key={q.id} className="space-y-2">
          <p className="text-sm font-medium text-foreground">{q.question}</p>
          {q.type === "text" ? (
            <Input
              placeholder={t("cardView.typeAnswer", { defaultValue: "Type your answer..." })}
              value={(answers[q.id] as string) || ""}
              onChange={(e) => setAnswer(q.id, e.target.value)}
              className="h-9 text-sm"
            />
          ) : q.type === "single" ? (
            <div className="flex flex-wrap gap-2">
              {q.options
                .filter((o) => o.trim())
                .map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setAnswer(q.id, option)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      answers[q.id] === option
                        ? "border-primary bg-primary/10 text-primary font-medium"
                        : "border-border text-foreground hover:border-primary/30"
                    }`}
                  >
                    {option}
                  </button>
                ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {q.options
                .filter((o) => o.trim())
                .map((option) => {
                  const selected = ((answers[q.id] as string[]) || []).includes(option);
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => toggleMulti(q.id, option)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        selected
                          ? "border-primary bg-primary/10 text-primary font-medium"
                          : "border-border text-foreground hover:border-primary/30"
                      }`}
                    >
                      {option}
                    </button>
                  );
                })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default RsvpPreferenceForm;
